import { supabase } from './supabase';

export interface CacheValueWithMeta<T> {
  value: T | null;
  updatedAt: number | null;
}

const parseUpdatedAt = (updatedAt: string | null | undefined): number | null => {
  if (!updatedAt) return null;
  const parsed = Date.parse(updatedAt);
  return Number.isFinite(parsed) ? parsed : null;
};

export const getGlobalCacheWithMeta = async <T>(key: string): Promise<CacheValueWithMeta<T>> => {
  if (!key) return { value: null, updatedAt: null };
  const { data, error } = await supabase
    .from('sal_global_cache')
    .select('value, updated_at')
    .eq('key', key)
    .maybeSingle();

  if (error) return { value: null, updatedAt: null };

  return {
    value: (data?.value as T) ?? null,
    updatedAt: parseUpdatedAt(data?.updated_at)
  };
};

export const getGlobalCache = async <T>(key: string): Promise<T | null> => {
  const cached = await getGlobalCacheWithMeta<T>(key);
  return cached.value;
};

export const setGlobalCache = async (key: string, kind: string, value: unknown) => {
  if (!key) return;
  await supabase
    .from('sal_global_cache')
    .upsert({ key, kind, value, updated_at: new Date().toISOString() }, { onConflict: 'key' });
};

export const getStoreCache = async <T>(storeId: string, key: string, kind: string): Promise<T | null> => {
  const cached = await getStoreCacheWithMeta<T>(storeId, key, kind);
  return cached.value;
};

export const getStoreCacheWithMeta = async <T>(storeId: string, key: string, kind: string): Promise<CacheValueWithMeta<T>> => {
  if (!storeId || !key) return { value: null, updatedAt: null };
  const { data, error } = await supabase
    .from('sal_store_cache')
    .select('value, updated_at')
    .eq('store_id', storeId)
    .eq('key', key)
    .eq('kind', kind)
    .maybeSingle();

  if (error) return { value: null, updatedAt: null };

  return {
    value: (data?.value as T) ?? null,
    updatedAt: parseUpdatedAt(data?.updated_at)
  };
};

export const setStoreCache = async (storeId: string, key: string, kind: string, value: unknown) => {
  if (!storeId || !key) return;
  await supabase
    .from('sal_store_cache')
    .upsert(
      { store_id: storeId, key, kind, value, updated_at: new Date().toISOString() },
      { onConflict: 'store_id,key,kind' }
    );
};
